import { Entity } from '@backstage/catalog-model';
import { PROJECT_SLUG } from '../catalog/component/ComponentPageContext';

/**
 * The CI snapshot as `github-insights` serves it (`/ci-health`): one entry per
 * repository that built often enough in the window to judge, collected by the
 * backend's poll and read here without asking GitHub anything.
 *
 * Read by the CI health card and by the parts of the home page that say where a
 * build is broken — the attention card, the workspace figure and the recently
 * used table — so they all describe one snapshot.
 */
export type RepoCiHealth = {
  slug: string;
  org: string;
  runs: number;
  successRate: number;
  avgDurationSec: number;
  isRed: boolean;
  isSlow: boolean;
  lastRun?: {
    conclusion: string | null;
    status: string | null;
    htmlUrl: string;
    headBranch: string | null;
    createdAt: string;
  };
  /**
   * True for a customer project's row (DX-681), absent for every other: the
   * backend marks the repositories under `githubInsights.customerProjects`.
   */
  customerProject?: boolean;
};

export type CiHealthSnapshot = {
  generatedAt: string | null;
  orgs: string[];
  /** Days of runs the figures cover; absent from a snapshot that predates it. */
  windowDays?: number;
  totals: {
    repos: number;
    runs: number;
    successRate: number;
    avgDurationSec: number;
  };
  repos: RepoCiHealth[];
};

/** The repository an entity builds in, lower-cased for comparison. */
export const slugOf = (entity: Entity): string | undefined =>
  entity.metadata.annotations?.[PROJECT_SLUG]?.toLowerCase();

/**
 * What the snapshot says about one repository's build.
 *
 * Not "health": nothing in the portal measures whether a service is up, and a
 * component page that the status page does not measure shows no health at all
 * (specs/component-page.md AC-6). What is measured is the build — whether the
 * default branch's latest finished run passed — so that is what this names, and
 * every way of not knowing is kept apart from a pass, because "we could not
 * tell" and "it passes" are different facts (specs/home-page.md AC-25).
 */
export type CiVerdict =
  | { kind: 'loading' }
  | { kind: 'failing'; label: string; hint: string }
  | { kind: 'passing'; label: string; hint: string }
  | { kind: 'not-known'; label: string; hint: string };

export const ciVerdictOf = (
  slug: string | undefined,
  ci: { loading: boolean; error?: Error; data?: CiHealthSnapshot },
): CiVerdict => {
  if (!slug) {
    return {
      kind: 'not-known',
      label: 'No repository',
      hint: 'The catalog names no GitHub repository for this component.',
    };
  }
  if (ci.loading) return { kind: 'loading' };
  if (ci.error || !ci.data) {
    return {
      kind: 'not-known',
      label: 'Not known',
      hint: 'The CI snapshot could not be loaded.',
    };
  }
  if (!ci.data.generatedAt) {
    return {
      kind: 'not-known',
      label: 'Not known yet',
      hint: 'The first CI snapshot is still being collected.',
    };
  }
  const repo = ci.data.repos.find(r => r.slug.toLowerCase() === slug);
  if (!repo) {
    return {
      kind: 'not-known',
      label: 'Too few runs',
      hint: 'The repository built too rarely in the window to judge, or is not in a watched organisation.',
    };
  }
  return repo.isRed
    ? {
        kind: 'failing',
        label: 'Failing',
        hint: "The default branch's latest finished run failed.",
      }
    : {
        kind: 'passing',
        label: 'Passing',
        hint: "The default branch's latest finished run passed.",
      };
};
